import {
  EmailIngestion,
  EmailIngestionFormValues,
  MailConnectionOverride,
  SftpConnectionOverride,
  SftpIngestion,
  SftpIngestionFormValues,
  UpdateEmailIngestionRequest,
  UpdateSftpIngestionRequest,
} from "../types";
import { EMPTY_EMAIL_FORM, EMPTY_SFTP_FORM } from "./validation";

// Blank inputs are left out of the request so the API copies (create) or keeps
// (update) the stored value. Passwords are sent exactly as typed.
const trimmedOrUndefined = (value: string): string | undefined =>
  value.trim() ? value.trim() : undefined;

const exactOrUndefined = (value: string): string | undefined =>
  value === "" ? undefined : value;

const withoutUndefined = <T extends object>(value: T): T | undefined => {
  const entries = Object.entries(value).filter(([, v]) => v !== undefined);
  return entries.length > 0 ? (Object.fromEntries(entries) as T) : undefined;
};

/** The API never returns credentials, so they always start blank. */
export const sftpFormValuesFrom = (
  ingestion: SftpIngestion | undefined,
): SftpIngestionFormValues =>
  ingestion
    ? {
        ...EMPTY_SFTP_FORM,
        description: ingestion.description,
        enabled: ingestion.enabled,
        sourceDirectory: ingestion.sourceDirectory,
        startDirectory: ingestion.sftpServer?.startDirectory ?? "",
        transferSubDirectory: ingestion.transferSubDirectory,
        upload: ingestion.upload,
      }
    : EMPTY_SFTP_FORM;

export const toSftpRequest = (
  values: SftpIngestionFormValues,
): UpdateSftpIngestionRequest => {
  const connection = withoutUndefined<SftpConnectionOverride>({
    hostName: trimmedOrUndefined(values.hostName),
    ipAddress: trimmedOrUndefined(values.ipAddress),
    portNumber: values.portNumber.trim()
      ? Number(values.portNumber)
      : undefined,
    userName: trimmedOrUndefined(values.userName),
    password: exactOrUndefined(values.password),
    hostKeyFingerPrint: trimmedOrUndefined(values.hostKeyFingerPrint),
  });
  const startDirectory = trimmedOrUndefined(values.startDirectory);

  return {
    description: values.description.trim(),
    enabled: values.enabled,
    sourceDirectory: values.sourceDirectory.trim(),
    ...(startDirectory !== undefined && { startDirectory }),
    transferSubDirectory: values.transferSubDirectory,
    upload: values.upload,
    ...(connection !== undefined && { connection }),
  };
};

/** The API never returns credentials, so they always start blank. */
export const emailFormValuesFrom = (
  ingestion: EmailIngestion | undefined,
): EmailIngestionFormValues =>
  ingestion
    ? {
        ...EMPTY_EMAIL_FORM,
        description: ingestion.description,
        enabled: ingestion.enabled,
        folder: ingestion.folder,
        senderMask: ingestion.senderMask,
        attachmentMask: ingestion.attachmentMask,
        mailBoxDescription: ingestion.mailBox?.description ?? "",
        destinationFileShareId: ingestion.destinationFileShare?.id ?? null,
        destinationSftpId: ingestion.destinationSftp?.id ?? null,
      }
    : EMPTY_EMAIL_FORM;

export const toEmailRequest = (
  values: EmailIngestionFormValues,
): UpdateEmailIngestionRequest => {
  const connection = withoutUndefined<MailConnectionOverride>({
    address: trimmedOrUndefined(values.address),
    password: exactOrUndefined(values.password),
  });
  const mailBoxDescription = trimmedOrUndefined(values.mailBoxDescription);

  return {
    description: values.description.trim(),
    enabled: values.enabled,
    folder: values.folder.trim(),
    senderMask: values.senderMask.trim(),
    attachmentMask: values.attachmentMask.trim(),
    ...(mailBoxDescription !== undefined && { mailBoxDescription }),
    destinationFileShareId: values.destinationFileShareId,
    // The API accepts only one destination; choosing a file share replaces an SFTP one.
    destinationSftpId:
      values.destinationFileShareId !== null ? null : values.destinationSftpId,
    ...(connection !== undefined && { connection }),
  };
};
